import { noop } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { useEffect } from 'react';
import { BasicDomTarget, getTargetElement } from '../utils/dom-target';
import { getScrollPosition } from '../utils/scroll-position';

export type ScrollListenController = (val: Position) => boolean;
export type Position = { left: number; top: number };

/**
 * 监听 document & element 滚动
 * 理论上 document的监听和element元素的监听是分开的，相互不会影响的，但在iphone中element元素的滚动会触发document的监听
 * 例如：一个自然页面，监听页面的滚动，即对document的滚动监听，但是页面中一个弹窗中设置了overflow=scroll，在弹窗内容滚动时，会触发document的滚动监听
 * @param callback
 * @param scrollTarget 滚动Dom或者Ref
 * @param shouldUpdate
 */
export const useScrollCallback = (
  callback: (position: Position) => void,
  scrollTarget?: BasicDomTarget<Element | Document>,
  shouldUpdate: ScrollListenController = () => true,
) => {
  const shouldUpdateRef = hooks.useCallbackRef(shouldUpdate);
  const throttleCallback = hooks.useThrottleCallback(callback);
  const el = getTargetElement(scrollTarget, document);
  const updatePosition = hooks.useCallbackRef(() => {
    const position: Position = getScrollPosition(el as Element);
    if (shouldUpdateRef(position)) {
      throttleCallback(position);
    }
  });

  useEffect(() => {
    if (el) {
      el.addEventListener('scroll', updatePosition);
      return () => {
        el.removeEventListener('scroll', updatePosition);
      };
    }
    return noop;
  }, [shouldUpdateRef, el, throttleCallback, updatePosition]);
};
